import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2a7 7 0 0 1-6.6-4.8H1.4v3.1A11.9 11.9 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.4 14.5a7.1 7.1 0 0 1 0-4.5V6.9H1.4a11.9 11.9 0 0 0 0 10.7l4-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A11.6 11.6 0 0 0 12 0 11.9 11.9 0 0 0 1.4 6.9l4 3.1A7 7 0 0 1 12 4.8z" />
  </svg>
);

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="mx-auto flex max-w-6xl flex-col gap-12 px-5 py-16 sm:px-8 lg:flex-row lg:items-center">
    <div className="lg:w-1/2">
      <img
        src="/logo.png"
        alt="ChatMiLu"
        data-testid="brand-logo-auth"
        className="h-20 w-auto drop-shadow-[0_0_30px_rgba(26,125,255,0.25)] sm:h-24"
      />
      <p className="mt-2 max-w-md font-display text-base text-ink-soft md:text-lg">
        Connect, Share, Talk, and Converse with ChatMiLu.
      </p>
      <p className="mt-8 max-w-sm text-sm text-ink-soft">
        One account unlocks the multilingual AI chat and TransMessages translated rooms across 50 languages.
      </p>
    </div>
    <div className="rounded-3xl border border-white/10 bg-panel p-8 shadow-sm lg:w-1/2">
      <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>
      {children}
    </div>
  </div>
);

export const GoogleButton = ({ testid }) => (
  <Button
    data-testid={testid}
    variant="outline"
    type="button"
    className="min-h-[48px] w-full rounded-full border-white/20 active:scale-95"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/chat";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
  >
    <GoogleMark />
    <span className="ms-2">Continue with Google</span>
  </Button>
);

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Welcome back to ChatMiLu");
      const pending = localStorage.getItem("cml_pending_join");
      if (pending) {
        localStorage.removeItem("cml_pending_join");
        nav(`/join/${pending}`);
      } else {
        nav(location.state?.from || "/chat");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page">
      <AuthShell title={t("login")} subtitle="Use Google or your email address.">
        <div className="mt-6 space-y-4">
          <GoogleButton testid="google-login-button" />
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-ink-soft">
            <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" data-testid="login-email-input" type="email" required
                className="mt-1.5 min-h-[44px] rounded-xl"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" data-testid="login-password-input" type="password" required
                className="mt-1.5 min-h-[44px] rounded-xl"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-xl bg-destructive/10 px-4 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit" type="submit" disabled={busy}
              className="min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
            >
              {busy ? "Signing in…" : t("login")}
            </Button>
          </form>
          <p className="text-sm text-ink-soft">
            New here?{" "}
            <Link to="/signup" data-testid="goto-signup" className="font-semibold text-brand">
              Create a free account
            </Link>
          </p>
        </div>
      </AuthShell>
    </div>
  );
}
